import React, { useState, useEffect } from "react";
import * as pb from "../generated/catalog_pb";
import { catalogClient } from "../services/grpc";
import { Button } from "../comp/ui/button";
import { ChevronDown, ChevronUp, Car } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { MarkSelector } from "./MarkSelector";
import { Category } from "./CategorySidebar";

export interface Model {
  model_id: number;
  name: string;
}
interface ModelProp{
  categories: Category[];
}

export default function ModelsSection({categories}:ModelProp){
  const [selectedCategory, setSelectedCategory] = useState<Category>(categories[0]);
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [selectedMark, setSelectedMark] = useState<string>("all");
  const navigate = useNavigate();
  const itemsPerRow = 6;
  const { id } = useParams<{ id?: string }>();
  const { model } = useParams<{ model?: string }>();
  useEffect(() => {
    fetchModels(selectedMark);
    setExpanded(false);
  }, [selectedMark]);
    useEffect(() => {
    const categoryCode = id?.trim() || "all";
    const found = categories.find(c => c.code === categoryCode) || categories[0];
    setSelectedCategory(found);
  }, [id]);
  const fetchModels = async (mark: string) => {
    setLoading(true);
    try {
      const req = new pb.ModelListReq();
      req.setMark(mark);

      const response = await new Promise<pb.ModelRes>((resolve, reject) => {
        catalogClient.getModelList(req, {}, (err, res) => {
          if (err) reject(err);
          else resolve(res);
        });
      });

      const list = response.getListList().map((item) => ({
        model_id: item.getModelId(),
        name: item.getName(),
      }));

      setModels(list);
    } catch (error) {
      console.error("Error loading models:", error);
    } finally {
      setLoading(false);
    }
  };

  const visibleModels = expanded ? models : models.slice(0, itemsPerRow);
  const hasMore = models.length > itemsPerRow;

  return (
    <div className="py-8 bg-white border-b">
      <div className="container mx-auto px-6">
        <MarkSelector
          selectedMark={selectedMark}
          onMarkSelect={setSelectedMark}
        />
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-blue-900">
            {selectedMark === "all" ? "Все модели" : selectedMark}
            {!loading && (
              <span className="text-base font-normal text-gray-500 ml-2">
                ({models.length})
              </span>
            )}
          </h2>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-10 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-100 animate-pulse rounded-xl" />
            ))}
          </div>
        ) : models.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Модели не найдены</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-10 gap-4">
              {visibleModels.map((model) => (
                <div
                  key={model.name}
                  className="group flex flex-col items-center justify-center p-4 border border-gray-100 hover:border-blue-200 rounded-2xl hover:bg-blue-50 transition-all cursor-pointer"
                  onClick={() => navigate(`/${selectedCategory.code}/model/${model.name}`)}
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3 group-hover:bg-blue-200 transition-colors">
                    <Car className="w-6 h-6 text-blue-600" />
                  </div>
                  <p className="text-center text-sm font-medium text-gray-800 line-clamp-2">
                    {model.name}
                  </p>
                </div>
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-8">
                <Button
                  variant="outline"
                  onClick={() => setExpanded(!expanded)}
                  className="flex items-center gap-2 rounded-full px-6"
                >
                  {expanded ? (
                    <>
                      Скрыть <ChevronUp className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      Показать все ({models.length}) <ChevronDown className="w-4 h-4" />
                    </>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}